<template>
  <div id="inbox">
    <div id="inbox-control">
      <div class="inbox-control-header">
        <h4>Članovi</h4>
        <button
          class="btn btn-primary"
          @click="showModal('ModalContentInboxMembers')"
        >
          <i class="fas fa-plus"></i>
        </button>
      </div>
      <input
        class="form-control form-control-lg"
        id="search-input"
        autocomplete="off"
        placeholder="Pretraga"
        v-model="searchCriteria"
        type="text"
      />
      <div class="inbox-members-wrapper" v-if="!membersLoading">
        <basic-info
          class="inbox-member"
          v-for="message in filteredList"
          :class="{
            'chat-active': message.post.id === getActiveChatID,
            'message-read': message.unread_message_counter == '0'
          }"
          @click.native="openChat(message)"
          :key="message.post.id"
          :profilePictureURL="baseURL + message.profile_pic_URL.link"
          :name="message.user_full_name"
          :description="message.communityName"
          :date="message.post.date"
          :isChat="true"
        ></basic-info>
      </div>
      <spinner
        v-if="membersLoading"
        class="spinner"
        line-fg-color="#00796b"
        :size="100"
        :l9ine-size="6"
      />
    </div>
    <div id="messages-window">
      <div class="inactive-chat" v-if="activeChatID === null">
        <h5>Izaberite člana zajednice sa kojim želite da razgovarate</h5>
      </div>
      <div class="messages" v-if="activeChatID !== null">
        <div class="messages-wrapper" v-if="!isLoading">
          <h5 id="chat-empty" v-if="activeChat.length === 0">
            Nemate poruka sa ovim članom
          </h5>
          <comment
            v-for="comment in activeChat"
            :comment="comment"
            :key="comment.comment_id"
            :isChatMessage="true"
          ></comment>
        </div>
        <spinner
          class="spinner"
          v-if="isLoading"
          line-fg-color="#00796b"
          :size="100"
          :line-size="6"
        ></spinner>
      </div>
      <div v-if="activeChatID !== null">
        <div class="message-input" v-if="isActiveChatUser">
          <textarea
            ref="messageTextArea"
            class="form-control"
            rows="1"
            placeholder="Unesite poruku"
            v-model="message"
            :style="{ height: messageTextAreaHeight + 'px' }"
            @input="autoresizeTextarea"
            @keydown.enter="handleEnter"
          ></textarea>
          <button
            :disabled="message.length === 0 || messageSending === true"
            class="btn btn-primary btn--mod"
            @click="sendMessage"
          >
            <slot>
              <spinner
                :size="20"
                line-fg-color="#00796b"
                v-if="messageSending"
              />
            </slot>
            <slot v-if="!messageSending">Pošalji</slot>
          </button>
        </div>
        <div v-else>
          <input
            ref="messageTextArea"
            class="form-control"
            rows="1"
            placeholder="Korisnik više nije član zajednice"
            :style="{ height: messageTextAreaHeight + 'px' }"
            disabled
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/axios/axios.js';
import BasicInfo from '@/components/common/BasicInfo.vue';
import Comment from '@/components/common/Comment.vue';
import { mapMutations, mapActions } from 'vuex';

const util = {
  getReceiverId(message, managerID) {
    if (message.post.id == 'fictive') {
      return message.id;
    } else if (message.post.member_id == managerID) {
      return message.post.direct_post_receiver_id;
    } else {
      return message.post.member_id;
    }
  }
};

export default {
  created() {
    this.baseURL = process.env.VUE_APP_OBJECT_STORAGE_URL;
    this.membersLoading = true;
    this.getChatMembers();
  },
  mounted() {
    this.chatElement = this.$el.querySelector('.messages');
  },
  beforeDestroy() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  },
  components: {
    BasicInfo,
    Comment
  },
  watch: {
    getFictiveChat() {
      this.handleFictiveChatOpening();
    },
    activeChat() {
      this.adjustChatHeight();
    },
    getNumberOfUnreadMessages() {
      this.getChatMembers();
    }
  },
  computed: {
    getFictiveChat() {
      return this.$store.state.fictiveChat;
    },
    getNumberOfUnreadMessages() {
      return this.$store.getters.getNumberOfUnreadMessages;
    },
    getActiveChatID() {
      return this.activeChatID;
    },
    isLoading() {
      return this.$store.state.loading;
    },
    filteredList() {
      if (this.searchCriteria === '') {
        return this.receivedMessages;
      } else {
        return this.receivedMessages.filter(msg => {
          if (
            msg.user_full_name
              .toLowerCase()
              .includes(this.searchCriteria.toLowerCase()) ||
            msg.communityName
              .toLowerCase()
              .includes(this.searchCriteria.toLowerCase())
          ) {
            return msg;
          }
        });
      }
    }
  },
  methods: {
    ...mapActions('modal', ['showFailModal']),
    ...mapMutations('modal', ['showModal']),

    handleEnter(e) {
      if (e.ctrlKey) {
        this.sendMessage();
      }
    },
    autoresizeTextarea(event) {
      event.target.style.height = 'auto';
      event.target.style.height = event.target.scrollHeight + 'px';
      if (event.target.scrollHeight > 110) {
        event.target.style.height = '110px';
      }
    },
    async handleFictiveChatOpening() {
      if (this.$store.state.fictiveChat !== null) {
        if (this.firstLoad) {
          for (const message of this.receivedMessages) {
            if (
              this.isMemberInInboxMessages(
                this.$store.state.fictiveChat.id,
                this.$store.state.fictiveChat.communityId,
                message
              )
            ) {
              this.openChat(message);
              this.firstLoad = false;
              return;
            }
          }
        }
        for (let m of this.receivedMessages) {
          if (
            m.post.member_id === this.$store.state.fictiveChat.id ||
            m.post.direct_post_receiver_id === this.$store.state.fictiveChat.id
          ) {
            this.openChat(m);
            return;
          }
        }
        this.openChat(this.$store.state.fictiveChat);
      }
    },
    async getChatMembers() {
      const chats = [];
      // for (const community of this.$store.state.communities.communities) {
      // chats.push(this.fetchCommunityMessages());
      // }
      this.receivedMessages = await this.fetchCommunityMessages();
      this.membersLoading = false;

      // this.receivedMessagesBuffer = [];
      // await Promise.all(chats)
      //   .then(() => {
      //     this.receivedMessages = this.receivedMessagesBuffer;
      //   })
      //   .catch(err => {
      //     console.log(err);
      //   })
      //   .finally(() => {
      //     this.membersLoading = false;
      //   });

      if (this.firstLoad && this.$store.state.fictiveChat) {
        this.handleFictiveChatOpening();
      }
    },
    async fetchCommunityMessages() {
      try {
        const data = await axios.post(
          '/post/get-direct-messages-inbox-manager',
          {
            num_of_postos: 50,
            timestamp: ''
          }
        );
        if (data.data.inbox_list.length > 0) {
          for (const inboxMessage of data.data.inbox_list) {
            const community = this.$store.state.communities.communities.find(
              c => c.community_id == inboxMessage.community_id
            );
            inboxMessage.communityName =
              community.address + ' ' + community.street_number;
            inboxMessage.communityId = community.community_id;
          }
          return data.data.inbox_list;
          // this.orderInboxMessagesByDate(true);
        }
        return [];
      } catch (error) {
        console.log(error);
      }
    },
    orderInboxMessagesByDate(buffer = false) {
      if (!buffer) {
        this.receivedMessages.sort(function(a, b) {
          return new Date(b.post.date) - new Date(a.post.date);
        });
      } else {
        this.receivedMessagesBuffer.sort(function(a, b) {
          return new Date(b.post.date) - new Date(a.post.date);
        });
      }
    },
    sendMessage() {
      if (this.messageSending === true || this.message === '') {
        return;
      }
      if (this.message.length > 1000) {
        this.showFailModal('Poruka ne može imati preko 1000 karaktera');
        return;
      }

      this.messageSending = true;
      axios
        .post('post', {
          body: this.message,
          community_id: this.activeChatInfo.communityId,
          member_id: this.activeChatInfo.managerMemberId,
          post_type: 4,
          direct_post_receiver_id: this.activeChatInfo.receiverId
        })
        .then(({ data }) => {
          if (this.activeChatID !== 'fictive') {
            this.activeChatID = data.post_id;
            this.getChatMessages();
            // this.updateInboxMembers(data.post_date);
            this.getChatMembers();
            this.message = '';
            this.adjustChatHeight();
          } else {
            this.activeChatID = data.post_id;
            this.getChatMessages();
            this.getChatMembers();
            this.message = '';
          }
          this.messageTextAreaHeight = 40;
          this.messageSending = false;
        })
        .catch(err => {
          console.log(err);
          this.messageSending = false;
        });
    },
    updateInboxMembers(newDate) {
      for (let message of this.receivedMessages) {
        if (message.post.id === this.activeChatID) {
          message.post.date = newDate;
        }
      }
      this.orderInboxMessagesByDate();
    },
    adjustChatHeight() {
      this.$nextTick(() => {
        let chat = this.$el.querySelector('.messages');
        let isScrolledToBottom =
          chat.scrollHeight - chat.clientHeight <= chat.scrollTop + 1;
        if (!isScrolledToBottom) {
          chat.scrollTop = chat.scrollHeight - chat.clientHeight;
        }
      });
    },
    openChat(message) {
      this.$store.commit(
        'lowerUnreadMessages',
        parseInt(message.unread_message_counter)
      );

      if (this.receivedMessages.length == 0) {
        this.receivedMessages.push(message);
      }

      if (message.unread_message_counter != '0') {
        this.readMessage(message);
      }

      this.activeChatInfo = {
        communityId: message.communityId,
        managerMemberId: this.$store.getters.getCommunityById(
          message.communityId
        ).member_id,
        receiverId: util.getReceiverId(
          message,
          this.$store.getters.getCommunityById(message.communityId).member_id
        )
      };
      if (message.post.id === 'fictive') {
        if (this.receivedMessages[0].post.id === 'fictive') {
          this.receivedMessages[0] = message;
        } else {
          this.receivedMessages.unshift(message);
        }
      }

      this.activeChatID = message.post.id;
      this.initChat();
    },
    isMemberInInboxMessages(memberId, communityId, message) {
      if (this.receivedMessages.length == 0) return false;
      const ids = [
        message.post.direct_post_receiver_id,
        message.post.member_id
      ];

      if (message.communityId == communityId && ids.indexOf(memberId) != -1)
        return true;
      else return false;
    },
    initChat() {
      if (this.activeChatID !== 'fictive') {
        this.$store.commit('loadingStarted');
        this.getChatMessages(true).then(() => {
          this.$store.commit('loadingFinished');
          this.adjustChatHeight();
          this.startAutoRefresh();
        });
      } else {
        this.activeChat = [];
      }
    },
    readMessage(message) {
      axios
        .post('post/set-read-messages', {
          post_id: message.post.id
        })
        .then(() => {
          message.unread_message_counter = '0';
        })
        .catch(err => {
          console.log(err);
        });
    },
    startAutoRefresh() {
      this.refreshInterval = setInterval(() => {
        this.getChatMessages();
      }, 1000);
    },
    getChatMessages(shouldClearInterval) {
      if (shouldClearInterval && this.refreshInterval) {
        clearInterval(this.refreshInterval);
      }
      return new Promise((resolve, reject) => {
        axios
          .post('post/get-direct-messages-chat', {
            post_id: this.activeChatID,
            num_of_posts: 100
          })
          .then(({ data }) => {
            this.isActiveChatUser = data.chat_user.active == '1' ? true : false;
            if (!shouldClearInterval) {
              const lastMessageFromApi = new Date(data.inbox_list[0].post.date);
              const lastLocalMessage = new Date(
                this.activeChat[this.activeChat.length - 1].timestamp
              );
              // if there are new messages since last check
              if (lastMessageFromApi > lastLocalMessage) {
                let isMineMessage = this.$store.state.communities.communities.find(
                  c =>
                    c.member_id ===
                    data.inbox_list[0].post.direct_post_receiver_id
                );
                console.log('isNew', isMineMessage);

                if (typeof isMineMessage !== 'undefined') {
                  this.readMessage(data.inbox_list[0]);
                }

                this.activeChat = data.inbox_list
                  .map(el => {
                    return {
                      member_id: el.post.member_id,
                      full_name: data.chat_user.user_full_name,
                      user_id: data.chat_user.user_id,
                      link: this.$store.getters.isManager(el.post.member_id)
                        ? this.$store.state.user.user.profile_pic_URL
                            .link_to_thumb
                        : data.chat_user.profile_pic_URL.link_to_thumb,
                      timestamp: el.post.date,
                      comment_body: el.post.body,
                      comment_id: el.post.id
                    };
                  })
                  .reverse();
              }
            } else {
              this.activeChat = data.inbox_list
                .map(el => {
                  return {
                    member_id: el.post.member_id,
                    full_name: data.chat_user.user_full_name,
                    user_id: data.chat_user.user_id,
                    link: this.$store.getters.isManager(el.post.member_id)
                      ? this.$store.state.user.user.profile_pic_URL
                          .link_to_thumb
                      : data.chat_user.profile_pic_URL.link_to_thumb,
                    timestamp: el.post.date,
                    comment_body: el.post.body,
                    comment_id: el.post.id
                  };
                })
                .reverse();
            }
            resolve();
          })
          .catch(err => {
            if (this.activeChatID == 'fictive') {
              this.isActiveChatUser = true;
            }
            console.log(err);
            reject();
          });
      });
    }
  },
  data() {
    return {
      refreshInterval: null,
      receivedMessages: [], //poruke u levoj strani
      receivedMessagesBuffer: [],
      message: '',
      searchCriteria: '',
      baseURL: '',
      firstLoad: true,
      messageSending: false,
      activeChatID: null,
      activeChat: [],
      membersLoading: false,
      activeChatInfo: {},
      messageTextAreaFocused: false,
      messageTextAreaHeight: 40,
      isActiveChatUser: true
    };
  },
  destroyed() {
    this.$store.state.fictiveChat = null;
  }
};
</script>

<style lang="scss">
#inbox {
  display: flex;
  flex-wrap: nowrap;
  width: 100%;
  // height: 100%;
  height: 100vh;
  padding: 10px 5px 10px 0;

  .spinner {
    margin-top: 100px;
    width: 100%;
  }

  #inbox-control {
    display: flex;
    flex-direction: column;
    flex-shrink: 3;
    /*width: auto;*/
    min-width: 30%;
    max-width: 30%;
    height: 100%;

    padding-right: 10px;
    padding-left: 10px;

    .message-read {
      h5 {
        font-weight: normal;
        color: #636363;
        span {
          display: none;
        }
      }
      h6 {
        color: #636363;
      }
    }

    .inbox-control-header {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
      padding: 5px 0;
      h4 {
        padding-left: 10px;
      }
      button {
        margin: 0;
      }

      button:focus,
      button:active {
        box-shadow: none;
      }
    }

    .chat-active {
      background-color: #d1d1d1;
    }

    .inbox-member {
      cursor: pointer;
      padding: 10px;
    }

    .inbox-member:hover {
      background-color: #d1d1d1;
    }

    .inbox-members-wrapper {
      flex: 0 0 auto;
      overflow: auto;
      max-height: calc(100% - 120px);
    }

    #search-input {
      width: 100%;
      height: 70px;
      border: none;
      border-radius: 0;
      margin-bottom: 10px;
      border-bottom: 2px solid transparent;
      transition: 0.5s border-bottom;
      background-color: #eeeeee;
    }

    #search-input:focus {
      box-shadow: none;
      border-bottom: 2px solid var(--primary-color);
    }
  }

  #messages-window {
    flex-shrink: 5;
    flex-grow: 5;
    max-height: 100%;
    border-left: 1px solid var(--primary-color);
    padding: 5px 0 10px 10px;
    max-width: 70%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    .messages {
      // max-height: calc(100% - 146px);
      // height: calc(100% - 146px);
      width: 100%;
      padding-right: 15px;
      overflow-y: auto;
      /*padding-bottom: 10px;*/

      #chat-empty {
        margin-top: 200px;
        text-align: center;
      }
    }

    .message-input {
      display: flex;
      height: auto;
      padding-right: 10px;
      margin-top: 15px;
      textarea {
        resize: vertical;
      }
      .btn--mod {
        height: 40px;
        min-width: 75px;
        margin-left: 5px;
        align-self: flex-end;
      }

      button:disabled {
        background-color: var(--primary-color-lighten);
      }
    }
    .inactive-chat {
      text-align: center;
      margin-top: 200px;
    }
  }
}
</style>
